import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import {
  Layout,
  Tag,
  Form,
  Input,
  Button,
  notification,
  Popconfirm,
} from "antd";

// Components
import Wrapper from "../components/wrapper";
import ServerSideTable from "../components/table/serverSideTable";

// Actions
import { fetchNewRiders } from "../redux/actions/newRiders";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axiosInstance from "../helpers/axios";

const { Content: LayoutContent } = Layout;
const { Search } = Input;

const formatDate = (date) => {
  const dateString = date;
  const formattedDate = moment
    .utc(dateString)
    .tz("Asia/Manila")
    .format("MMM D, YYYY hh:mm A");
  return formattedDate;
};

const NewRiders = () => {
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [delaySearchText, setDelaySearchText] = useState("");
  const [riderStatusLoading, setRiderStatusLoading] = useState("");
  const [removeDeviceLoading, setRemoveDeviceLoading] = useState(false);
  const [riderPasswordResetLoading, setRiderPasswordResetLoading] =
    useState(false);
  const {
    loading,
    newRidersData,
    totalRecord,
    nextPage,
    previousPage,
    currentPage,
    userInfo,
  } = useSelector(
    ({ newRidersReducers, userLoginReducer }) => ({
      userInfo: userLoginReducer.userInfo,
      newRidersData: newRidersReducers.data,
      totalRecord: newRidersReducers.totalRecord,
      nextPage: newRidersReducers.nextPage,
      previousPage: newRidersReducers.previousPage,
      currentPage: newRidersReducers.currentPage,
      loading: newRidersReducers.loading,
    }),
    isEqual
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelaySearchText(searchText);
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText]);

  useEffect(() => {
    const payload = {
      page: 1,
      limit: 15,
      searchText: delaySearchText,
    };
    dispatch(fetchNewRiders(payload));
  }, [delaySearchText, dispatch]);

  const onSearch = () => {
    const payload = {
      page: 1,
      limit: 15,
      searchText,
    };

    dispatch(fetchNewRiders(payload));
  };
  const resetRiderPassword = async (riderUserId) => {
    try {
      setRiderPasswordResetLoading(true);
      const response = await axiosInstance(userInfo.token).patch(
        `/api/password-reset/${riderUserId}`
      );

      if (response.data.success === true) {
        notification.success({
          message: response.data.message,
        });
      }
      setRiderPasswordResetLoading(false);
    } catch (error) {
      setRiderPasswordResetLoading(false);
      console.log("Password Reset Error :  ----->", error);
    }
  };
  const removeDeviceId = async (riderUserId) => {
    try {
      setRemoveDeviceLoading(true);
      const response = await axiosInstance(userInfo.token).put(
        `/api/rider-logout/${riderUserId}`
      );

      if (response.data.success === true) {
        notification.success({
          message: response.data.message,
        });
      }
      setRemoveDeviceLoading(false);
    } catch (error) {
      setRemoveDeviceLoading(false);
      console.log("removeDeviceId ----->", error);
    }
  };

  const customFilters = () => {
    return (
      <div className="mb-2">
        <h1 className="text-xl">All Riders</h1>
        <div
          className="p-2 flex justify-between"
          style={{ backgroundColor: "#fafafa" }}
        >
          <Form layout="inline">
            <Form.Item label="Search">
              <Search
                placeholder="input search text"
                allowClear
                style={{ width: "15VW" }}
                value={searchText}
                type="secondary"
                size="medium"
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                onSearch={onSearch}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    );
  };

  const defaultPage = useMemo(() => {
    return {
      page: 1,
      limit: 15,
    };
  }, []);

  const onClickNextPage = () => {
    dispatch(
      fetchNewRiders({
        ...nextPage,
        searchText,
      })
    );
  };

  const onClickPreviousPage = () => {
    dispatch(
      fetchNewRiders({
        ...previousPage,
        searchText,
      })
    );
  };

  const onClickFirstPage = () => {
    dispatch(
      fetchNewRiders({
        ...defaultPage,
        searchText,
      })
    );
  };

  const onClickLastPage = () => {
    dispatch(
      fetchNewRiders({
        page: Math.ceil(totalRecord / 15),
        limit: 15,
        searchText,
      })
    );
  };

  const onChangeRiderDutyStatus = async (userID) => {
    try {
      setRiderStatusLoading(true);
      const response = await axiosInstance(userInfo?.token).put(
        `/api/rider-active-status/${userID}/offline`
      );
      if (response.data.activated) {
      }
      dispatch(fetchNewRiders(defaultPage));
      setRiderStatusLoading(false);
    } catch (error) {
      console.log(
        "Error activating active status:",
        error.response ? error.response.data : error.message
      );
      setRiderStatusLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        Cell: ({ row: { original } }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div>
              <Popconfirm
                title="Are you sure you want to reset the password?"
                onConfirm={() => resetRiderPassword(original.user_id)}
                okText="Yes"
                cancelText="No"
                placement="right"
              >
                <Button
                  style={{
                    backgroundColor: riderPasswordResetLoading
                      ? "gray"
                      : "tomato",
                    color: "white",
                  }}
                  loading={riderPasswordResetLoading}
                  disabled={riderPasswordResetLoading}
                  size="small"
                >
                  Reset Password
                </Button>
              </Popconfirm>
            </div>
            <div>
              <Popconfirm
                title="Are you sure you want to log out this rider from the Rider App?"
                onConfirm={() => removeDeviceId(original.user_id)}
                okText="Yes"
                cancelText="No"
                placement="right"
              >
                <Button
                  style={{
                    backgroundColor: riderPasswordResetLoading
                      ? "gray"
                      : "#6495ED",
                    color: "white",
                  }}
                  loading={removeDeviceLoading}
                  disabled={removeDeviceLoading}
                  size="small"
                >
                  Unlink Device
                </Button>
              </Popconfirm>
            </div>
            {original.active_status === "online" ? (
              <Button
                size="small"
                onClick={() => {
                  console.log(">>> original:", original);
                  onChangeRiderDutyStatus(original.user_id);
                }}
                loading={riderStatusLoading}
              >
                Restrict booking
              </Button>
            ) : (
              <text>Offline</text>
            )}
          </div>
        ),
        width: 100,
      },
      {
        Header: "Username",
        accessor: "user_name",
      },
      {
        Header: "Full Name",
        accessor: "fullName",
      },
      {
        Header: "Email",
        accessor: "email_address",
      },
      {
        Header: "Contact number",
        accessor: "contact_number",
      },
      {
        Header: "Address",
        accessor: "address",
      },
      {
        Header: "preferred delivery area",
        accessor: (row) => {
          return (
            <p
              style={{ color: "black" }}
            >{`${row.service_province}, ${row.service_city}`}</p>
          );
        },
      },
      {
        Header: "partner availability",
        accessor: (row) => {
          if (row.service_partner_id !== null) {
            return (
              <Tag icon={<CheckCircleOutlined />} color="success">
                With Partner
              </Tag>
            );
          } else {
            return (
              <Tag icon={<CloseCircleOutlined />} color="error">
                No Partner
              </Tag>
            );
          }
        },
      },

      {
        Header: "Date",
        accessor: (row) => formatDate(row.created_at),
      },
    ],
    []
  );

  useEffect(() => {
    dispatch(fetchNewRiders(defaultPage));
  }, [dispatch, defaultPage]);

  const renderContent = () => {
    return (
      <LayoutContent>
        <ServerSideTable
          columns={columns}
          data={newRidersData} //
          loading={loading}
          serverSidePagination={{
            totalRecord,
            onClickNextPage,
            onClickPreviousPage,
            currentPage,
            nextPage,
            previousPage,
            onClickFirstPage,
            onClickLastPage,
          }}
          customFilters={customFilters}
        />
      </LayoutContent>
    );
  };

  return (
    <>
      <Wrapper content={renderContent()} modal={<></>} />
    </>
  );
};

export default NewRiders;
